package com.essntl.features.client.ui.addeditclient.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.TransformOrigin
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import com.essntl.core.utils.utils.capitalizeFirstLetter
import com.essntl.features.client.domain.model.ClientModel
import com.essntl.features.client.ui.addeditclient.AddEditClientState
import com.essntl.features.client.ui.addeditclient.AddEditClientValidation
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.input.EsPhoneInput
import com.essntl.uikit.components.input.date.EsDateInput
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography

@Composable
fun ClientBaseInformation(
    state: AddEditClientState,
    validation: AddEditClientValidation,
    onClientChange: (ClientModel) -> Unit,
    enabled: Boolean,
) {
    val client = state.client.value
    val isLoading = state.addEditClient.isLoading

    var isExpanded by rememberSaveable { mutableStateOf(true) }

    Column(
        modifier =
            Modifier
                .fillMaxWidth()
                .background(
                    color = MaterialTheme.esColorScheme.bgSecondary,
                    shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
                )
                .padding(
                    vertical = 3.spacing,
                    horizontal = 2.5f.spacing,
                ),
    ) {
        Row(
            horizontalArrangement = Arrangement.SpaceBetween,
            modifier =
                Modifier
                    .fillMaxWidth(),
        ) {
            Text(
                text = "Base Information",
                color = MaterialTheme.esColorScheme.textPrimary,
                style = MaterialTheme.esTypography.s2SemiBold16,
            )
            EsLargeIcon(
                res = EsIcons.ArrowDown,
                modifier =
                    Modifier
                        .graphicsLayer {
                            transformOrigin = TransformOrigin.Center
                            rotationZ = if (isExpanded) 180f else 0f
                        }
                        .clip(CircleShape)
                        .pointerHoverIcon(PointerIcon.Hand)
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                        ) {
                            isExpanded = !isExpanded
                        },
            )
        }

        AnimatedVisibility(isExpanded) {
            Column(
                modifier =
                    Modifier
                        .fillMaxWidth(),
            ) {
                Spacer(modifier = Modifier.height(3.spacing))

                EsInput(
                    value = client.firstName ?: "",
                    onValueChange = {
                        onClientChange(
                            client.copy(
                                firstName = it,
                            ),
                        )
                    },
                    placeholder = "Enter first name",
                    label = "First name",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = client.lastName,
                    onValueChange = {
                        validation.addEditClientForm.lastName.validate(it)
                        onClientChange(
                            client.copy(
                                lastName = it,
                            ),
                        )
                    },
                    state = validation.addEditClientForm.lastName.state,
                    message = "This field is required",
                    placeholder = "Enter last name",
                    label = "Last name",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = client.salutation ?: "",
                    onValueChange = {
                        onClientChange(
                            client.copy(
                                salutation = it,
                            ),
                        )
                    },
                    placeholder = "Mrs & Mr Appleseed",
                    label = "Display name",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsDateInput(
                    value = client.dateOfBirth.orEmpty(),
                    onValueChange = {
                        onClientChange(
                            client.copy(dateOfBirth = (it)),
                        )
                    },
                    label = "Date of birth",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = client.notes ?: "",
                    onValueChange = {
                        onClientChange(
                            client.copy(
                                notes = it,
                            ),
                        )
                    },
                    placeholder = "Enter notes",
                    label = "Notes",
                    singleLine = false,
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsPhoneInput(
                    value = client.telephone?.toLongOrNull() ?: 0,
                    onValueChange = { value ->
                        onClientChange(
                            client.copy(
                                telephone = value.toString(),
                            ),
                        )
                    },
                    placeholder = "Enter telephone",
                    label = "Telephone",
                    enabled = enabled && !isLoading,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = client.email ?: "",
                    onValueChange = {
                        onClientChange(
                            client.copy(
                                email = it,
                            ),
                        )
                    },
                    placeholder = "Enter email",
                    label = "Email",
                    enabled = enabled && !isLoading,
                )

                client.travelAgent?.let { travelAgent ->
                    Spacer(modifier = Modifier.height(2.spacing))

                    val prettyTravelAgent =
                        travelAgent.prettyName + ", " + (travelAgent.contact?.organization?.name?.capitalizeFirstLetter()
                            .orEmpty())

                    EsInput(
                        value = prettyTravelAgent,
                        onValueChange = {},
                        label = "Travel agent",
                        enabled = false,
                    )
                }
            }
        }
    }
}